import type { ChangeEvent } from 'react';

/**
 * Capital-letters-only text capture.
 *
 * Client rule: text typed on NHS forms (GOS / eGOS claims, NHS patient
 * details) and every practitioner / optometrist / performer name is stored in
 * CAPITALS. A CSS `text-transform` only changes how it looks, so the value
 * itself is converted as the user types; the backend normalises the same
 * fields again on save for older clients and direct API callers.
 *
 * Don't use this on emails, URLs, numbers, dates or ordinary clinical notes.
 */

type TextElement = HTMLInputElement | HTMLTextAreaElement;

/** Uppercases a string value; null/undefined (and non-strings) pass through. */
export function toUpperText<T>(value: T): T {
  return (typeof value === 'string' ? value.toUpperCase() : value) as T;
}

/**
 * Uppercases the element's current value in place, keeping the caret where it
 * was. Because the DOM already holds the new value by the time the form state
 * updates, React does not rewrite the input, so the caret does not jump to the
 * end when editing the middle of a word.
 */
export function uppercaseElementValue(element: TextElement): void {
  const { value } = element;
  const upper = value.toUpperCase();
  if (upper === value) return;

  // selectionStart is null for input types without a caret (email, number…).
  const start = element.selectionStart;
  const end = element.selectionEnd;
  element.value = upper;
  if (start === null || end === null) return;
  // A few characters grow when uppercased (ß → SS), so map the caret through
  // the uppercased prefix rather than reusing the raw offset.
  try {
    element.setSelectionRange(
      value.slice(0, start).toUpperCase().length,
      value.slice(0, end).toUpperCase().length,
    );
  } catch {
    // Element type without a selection API — the value is still uppercased.
  }
}

/**
 * Wraps a text field change handler (react-hook-form `field.onChange`, Formik
 * `handleChange`, or a `useState` setter wrapper) so the value it reads from
 * `event.target.value` is already uppercase.
 */
export function withUppercase<E extends ChangeEvent<TextElement>>(
  onChange: (event: E) => void,
): (event: E) => void {
  return (event: E) => {
    uppercaseElementValue(event.target);
    onChange(event);
  };
}
